import React, { createContext, useContext, useState } from 'react';
import PropTypes from 'prop-types';

// Create context
const WaitlistContext = createContext();

/**
 * Provider component for the waitlist modal
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components
 * @returns {JSX.Element} Provider component
 */
export const WaitlistProvider = ({ children }) => {
  const [isWaitlistOpen, setIsWaitlistOpen] = useState(false);
  const [waitlistUrl, setWaitlistUrl] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Function to open the waitlist sidebar (no URL = just show sidebar)
  const openWaitlist = () => {
    setIsWaitlistOpen(true);
    setSidebarOpen(true);
  };

  // Function to set the waitlist URL and open modal (opens waitlist if not already open)
  const setWaitlistUrlAndOpenModal = (url) => {
    setIsWaitlistOpen(true);
    setWaitlistUrl(url);
    // Don't set sidebarOpen to true - this means modal was opened directly
  };

  // Function to close the waitlist modal
  const closeWaitlist = () => {
    setIsWaitlistOpen(false);
    setSidebarOpen(false);
    // Clear the URL after a delay to allow the modal to close smoothly
    setTimeout(() => {
      setWaitlistUrl(null);
    }, 300);
  };

  // Function to clear just the URL (returns to sidebar from modal)
  const clearWaitlistUrl = () => {
    setWaitlistUrl(null);
  };

  return (
    <WaitlistContext.Provider
      value={{
        isWaitlistOpen,
        waitlistUrl,
        sidebarOpen,
        openWaitlist,
        closeWaitlist,
        clearWaitlistUrl,
        setWaitlistUrlAndOpenModal
      }}
    >
      {children}
    </WaitlistContext.Provider>
  );
};

WaitlistProvider.propTypes = {
  children: PropTypes.node.isRequired
};

/**
 * Custom hook to use the waitlist context
 * @returns {Object} Waitlist context
 */
export const useWaitlist = () => {
  const context = useContext(WaitlistContext);
  if (!context) {
    throw new Error('useWaitlist must be used within a WaitlistProvider');
  }
  return context;
};
